import { useCallback, useEffect, useState } from 'react'

import { useAccess } from '@/shared/access'
import { parseCursorPage } from '@/shared/api/paging'
import { useApiClient } from '@/shared/api/use-api-client'

import { readUserGroupsPermission, userGroupsApi } from './paths'
import { parseUserGroupSummary } from './user-group'

/** A group a person is in. */
export interface MemberGroup {
  readonly id: string
  readonly name: string
}

/** A person's groups as loaded: `hidden` when the viewer may not read groups. */
export type MemberGroups =
  | { readonly status: 'loading' }
  | { readonly status: 'hidden' }
  | { readonly status: 'error' }
  | { readonly status: 'ready'; readonly groups: readonly MemberGroup[] }

/** Every group a person is in, by name, a page at a time; again on `reload`. Nothing loads while `userId` is undefined. */
export function useMemberGroups(userId: string | undefined): { readonly loaded: MemberGroups; readonly reload: () => void } {
  const client = useApiClient()
  const access = useAccess()
  const mayRead = access.can(readUserGroupsPermission)
  const [loaded, setLoaded] = useState<MemberGroups>({ status: 'loading' })

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (userId === undefined) {
        return
      }
      try {
        const groups: MemberGroup[] = []
        let cursor: string | null = null
        do {
          const query = new URLSearchParams({ memberId: userId, sort: 'name', limit: '100' })
          if (cursor !== null) {
            query.set('cursor', cursor)
          }
          // oxlint-disable-next-line no-await-in-loop -- each page needs the cursor the one before it gave
          const page = parseCursorPage(await client.get(`${userGroupsApi}?${query.toString()}`, signal), parseUserGroupSummary)
          groups.push(...page.data.map((group) => ({ id: group.id, name: group.name })))
          cursor = page.pagination.nextCursor
        } while (cursor !== null)
        setLoaded({ status: 'ready', groups })
      } catch {
        if (signal?.aborted !== true) {
          setLoaded({ status: 'error' })
        }
      }
    },
    [client, userId],
  )

  useEffect(() => {
    if (access.status === 'loading') {
      return undefined
    }
    if (!mayRead) {
      // oxlint-disable-next-line react/set-state-in-effect -- the viewer's permissions arrived
      setLoaded({ status: 'hidden' })
      return undefined
    }
    const controller = new AbortController()
    void load(controller.signal)
    return () => controller.abort()
  }, [access.status, load, mayRead])

  const reload = useCallback(() => {
    setLoaded({ status: 'loading' })
    void load()
  }, [load])

  return { loaded, reload }
}
